import { lowerCase } from 'lodash-es'
import { DCFilter, DCFilterOperator, DCTableName, IEvent, SQLFilterOperator } from 'types'
import { getMomentId, LeadTableColumn } from './leadSessionUtils'

export const excludeFilters = [
  'created',
  'address',
  'address2',
  'duration',
  'email',
  'firstName',
  'followUpOwner',
  'lastName',
  'name',
  'notes',
  'phone',
]

interface FilterContext {
  field: string
  value?: string
  secondaryTargetField?: string
  secondaryTargetValue?: string
  parentTable: DCTableName
}

export function getFilterContext(filter: DCFilter, columns?: LeadTableColumn[]): FilterContext {
  const { field, table, secondaryTargetValue } = filter
  let filterContext: FilterContext = { field, parentTable: table || 'leadSessions' }
  const type =
    field === 'title' && table === 'sessionTraits'
      ? undefined
      : columns
        ? columns.find(col => col.key === field)?.source
        : undefined

  if (type) {
    switch (type) {
      case 'smart':
        filterContext.parentTable = 'smartProperties'
        filterContext.field = 'text'
        filterContext.secondaryTargetField = 'name'
        filterContext.value = field
        break

      case 'trait':
        filterContext.parentTable = 'sessionTraits'
        filterContext.field = 'title'
        filterContext.secondaryTargetField = 'groupTitle'
        filterContext.value = field
        break

      case 'primary':
      case 'helper':
        filterContext.parentTable = 'leads'
        break
    }
  } else {
    switch (field) {
      case 'text':
        filterContext.parentTable = 'smartProperties'
        filterContext.secondaryTargetField = 'name'
        filterContext.value = secondaryTargetValue
        break

      case 'title':
        filterContext.parentTable = 'sessionTraits'
        filterContext.secondaryTargetField = 'groupTitle'
        filterContext.value = secondaryTargetValue
        break
    }
  }

  return filterContext
}

export function getFilterKey(filter: DCFilter, columns?: LeadTableColumn[]): string {
  const ctx = getFilterContext(filter, columns)
  return ctx.secondaryTargetField && ctx.value ? ctx.value : ctx.field
}

export function getFiltersFromConditions(
  filterConditions: DCFilter[],
  columns?: LeadTableColumn[]
) {
  if (!filterConditions) return []

  const unique = new Map<string, DCFilter>()
  for (const filter of filterConditions) {
    const key = getFilterKey(filter, columns)
    if (!unique.has(key)) {
      unique.set(key, filter)
    }
  }
  return Array.from(unique.values())
}

export function getContextFiltersForAggregation(
  draftFilters: DCFilter[],
  selectedFilter: DCFilter,
  columns?: LeadTableColumn[]
): DCFilter[] {
  const orderedFilters = getFiltersFromConditions(draftFilters, columns)
  const selectedKey = getFilterKey(selectedFilter, columns)
  const selectedIndex = orderedFilters.findIndex(f => getFilterKey(f, columns) === selectedKey)
  const filtersBeforeSelected = selectedIndex >= 0 ? orderedFilters.slice(0, selectedIndex) : []
  const contextFilters = filtersBeforeSelected.filter(f => {
    if (f.value === '' || (Array.isArray(f.value) && f.value.length === 0)) return false
    return true
  })
  const byKey = new Map<string, DCFilter[]>()
  for (const f of contextFilters) {
    const key = getFilterKey(f, columns)
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key)!.push(f)
  }
  const result: DCFilter[] = []
  for (const [, filters] of byKey) {
    if (filters.length === 1) {
      result.push(filters[0])
    } else {
      const eqFilters = filters.filter(f => f.operator === 'eq')
      const values = eqFilters.map(f => f.value)
      if (values.length > 0) {
        result.push({
          ...filters[0],
          operator: 'in',
          value: values,
        })
      }
    }
  }
  return result
}

export function getFilterValueParams(field: string, label: string, event?: IEvent) {
  const context: {
    operator: DCFilterOperator | SQLFilterOperator
    value: string | number | boolean
  } = {
    operator: 'eq',
    value: label,
  }
  if (label === 'Unset') {
    context.operator = 'isNull'
    context.value = true
  } else {
    switch (field) {
      case 'temp': {
        context.value = lowerCase(label)
        break
      }
      case 'momentId': {
        context.value = getMomentId(event!, label)
        break
      }
      case 'optIn':
      case 'followUp': {
        context.value = label === 'Yes'
        break
      }
      default: {
        context.operator = 'ILIKE'
        break
      }
    }
  }
  return context
}
